import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as ListaIcon,
  FormatListNumbered as ListaNumIcon,
  FormatClear as LimpiarIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { asegurarAnclas, Titulo, titulosDe } from "../../utils/contenidoHtml";
import { tokens } from "../../theme";

/**
 * Editor de texto enriquecido del kit: escribe con formato y guarda HTML.
 *
 * Se apoya en `document.execCommand`, que está marcado como obsoleto pero sigue
 * siendo lo que implementan todos los navegadores; a cambio, el proyecto no suma
 * una dependencia de editor (TinyMCE/Quill pesan más que esta caja). Si algún día
 * hace falta tablas o imágenes, aquí es donde se reemplaza por una librería.
 *
 * El HTML que produce lo pinta `RichContent`, así que lo que se escribe se ve
 * igual en la ficha.
 *
 * Enlaces: además de una dirección web, se puede enlazar a un título de la misma
 * página. No hay que "crear" el ancla: cada título es una (`utils/contenidoHtml`
 * le pone su `id` al abrir el diálogo), y la página larga las usa para su índice.
 */

/**
 * True si el HTML no tiene texto. Al borrar todo, el navegador deja restos como
 * `<br>` o `<p></p>`, que no son contenido pero sí son distintos de "".
 */
export function htmlVacio(html: string): boolean {
  return !html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

const BOTONES = [
  { cmd: "bold", titulo: "Negrita", icono: <BoldIcon fontSize="small" /> },
  { cmd: "italic", titulo: "Cursiva", icono: <ItalicIcon fontSize="small" /> },
  { cmd: "insertUnorderedList", titulo: "Viñetas", icono: <ListaIcon fontSize="small" /> },
  { cmd: "insertOrderedList", titulo: "Lista numerada", icono: <ListaNumIcon fontSize="small" /> },
];

// Títulos: los mismos niveles que estiliza `RichContent` (h2 sección, h3 pregunta).
// Solo aparecen con `conTitulos`: en una descripción de tema estorbarían.
const TITULOS = [
  { bloque: "h2", titulo: "Título de sección", texto: "T1" },
  { bloque: "h3", titulo: "Subtítulo", texto: "T2" },
  { bloque: "p", titulo: "Texto normal", texto: "¶" },
];

export default function EditorTexto({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  minHeight = 160,
  maxHeight,
  conTitulos = false,
}: {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  /**
   * Tope de alto del texto: pasado ese punto scrollea la caja y no la página, y
   * la barra de herramientas queda siempre a la vista. Para textos largos.
   */
  maxHeight?: number | string;
  /** Añade títulos (h2/h3) a la barra: para páginas largas, no para un campo. */
  conTitulos?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Al abrir el diálogo el foco se va del editor y con él la selección: hay que
  // guardarse dónde estaba el cursor para poder aplicar el enlace ahí.
  const seleccion = useRef<Range | null>(null);
  const [enlazando, setEnlazando] = useState<Titulo[] | null>(null);

  // Solo se escribe en el DOM cuando el valor llega de fuera (carga inicial, edición
  // de otro tema). Comparar contra el HTML actual evita reescribir mientras se teclea,
  // que es lo que haría saltar el cursor al final.
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== value) el.innerHTML = value || "";
  }, [value]);

  const emitir = () => onChange(ref.current?.innerHTML ?? "");

  const ejecutar = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emitir();
  };

  const abrirEnlace = () => {
    const sel = window.getSelection();
    seleccion.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    // Fijar el ancla de cada título antes de listarlos: así el enlace apunta a un
    // `id` explícito y sigue funcionando aunque después se reescriba el título.
    if (ref.current && asegurarAnclas(ref.current)) emitir();
    setEnlazando(titulosDe(ref.current?.innerHTML ?? ""));
  };

  /** Devuelve el cursor a donde estaba antes de abrir el diálogo. */
  const restaurar = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && seleccion.current) {
      sel.removeAllRanges();
      sel.addRange(seleccion.current);
    }
    return sel;
  };

  const aplicarEnlace = (destino: string, textoSugerido: string) => {
    setEnlazando(null);
    const sel = restaurar();
    // Sin nada seleccionado no hay qué enlazar: se escribe el texto del destino.
    if (sel && !sel.isCollapsed) document.execCommand("createLink", false, destino);
    else document.execCommand("insertHTML", false, `<a href="${escapar(destino)}">${escapar(textoSugerido)}</a>`);
    emitir();
  };

  const quitarEnlace = () => {
    setEnlazando(null);
    restaurar();
    document.execCommand("unlink");
    emitir();
  };

  // Pegar como texto plano: si no, Word y Google Docs traen su propio HTML.
  const pegarPlano = (e: React.ClipboardEvent) => {
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    emitir();
  };

  const vacio = htmlVacio(value || "");

  return (
    <Box>
      {label && (
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block", mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {/* Sin `overflow: hidden`: haría de esta caja el contenedor de scroll de la
          barra y la barra dejaría de pegarse arriba. */}
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "5px", bgcolor: tokens.neutro.surface }}>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            // Pegada arriba de la caja: en un texto largo el que scrollea es el
            // diálogo, y con él se iban las herramientas.
            position: "sticky",
            top: 0,
            zIndex: 2,
            p: 0.5,
            bgcolor: tokens.neutro.surface,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderRadius: "5px 5px 0 0",
          }}
        >
          {conTitulos && (
            <>
              {TITULOS.map((t) => (
                <Herramienta key={t.bloque} titulo={t.titulo} disabled={disabled} onPulsar={() => ejecutar("formatBlock", `<${t.bloque}>`)}>
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1, px: 0.2 }}>
                    {t.texto}
                  </Box>
                </Herramienta>
              ))}
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            </>
          )}
          {BOTONES.map((b) => (
            <Herramienta key={b.cmd} titulo={b.titulo} disabled={disabled} onPulsar={() => ejecutar(b.cmd)}>
              {b.icono}
            </Herramienta>
          ))}
          <Herramienta titulo="Enlace" disabled={disabled} onPulsar={abrirEnlace}>
            <LinkIcon fontSize="small" />
          </Herramienta>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Herramienta titulo="Quitar formato" disabled={disabled} onPulsar={() => ejecutar("removeFormat")}>
            <LimpiarIcon fontSize="small" />
          </Herramienta>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {vacio && placeholder && (
            <Typography
              variant="body2"
              sx={{ position: "absolute", top: 12, left: 14, color: "text.disabled", pointerEvents: "none" }}
            >
              {placeholder}
            </Typography>
          )}
          <Box
            ref={ref}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emitir}
            onBlur={emitir}
            onPaste={pegarPlano}
            sx={{
              minHeight,
              maxHeight,
              p: "12px 14px",
              fontSize: 15,
              lineHeight: 1.6,
              outline: "none",
              overflowY: "auto",
              color: disabled ? "text.disabled" : "text.primary",
              "& p": { my: 0.75 },
              "& ul, & ol": { pl: 3, my: 0.75 },
              "& a": { color: "secondary.dark", fontWeight: 600 },
              // Los títulos se ven como en la página, para editar sin sorpresas.
              "& h2": { fontFamily: tokens.fuente.titulos, fontWeight: 700, fontSize: 22, color: tokens.marca.texto, mt: 2, mb: 1 },
              "& h3": { fontFamily: tokens.fuente.titulos, fontWeight: 600, fontSize: 17, mt: 1.5, mb: 0.5 },
            }}
          />
        </Box>
      </Box>

      {enlazando && (
        <DialogoEnlace titulos={enlazando} onCerrar={() => setEnlazando(null)} onAplicar={aplicarEnlace} onQuitar={quitarEnlace} />
      )}
    </Box>
  );
}

/** El HTML del enlace se arma a mano; lo que venga del usuario va escapado. */
function escapar(texto: string): string {
  return texto.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * A dónde lleva el enlace: a una dirección web o a un título de esta misma
 * página. Lo segundo solo aparece si hay títulos, que es cuando tiene sentido
 * (una descripción de tema no los tiene).
 */
function DialogoEnlace({
  titulos,
  onCerrar,
  onAplicar,
  onQuitar,
}: {
  titulos: Titulo[];
  onCerrar: () => void;
  onAplicar: (destino: string, textoSugerido: string) => void;
  onQuitar: () => void;
}) {
  const [modo, setModo] = useState<"url" | "ancla">("url");
  const [url, setUrl] = useState("https://");
  const [ancla, setAncla] = useState(titulos[0]?.id ?? "");

  const elegido = titulos.find((t) => t.id === ancla);
  const puede = modo === "url" ? url.trim() !== "" && url.trim() !== "https://" : !!elegido;

  const aplicar = () => {
    if (modo === "url") onAplicar(url.trim(), url.trim());
    else if (elegido) onAplicar(`#${elegido.id}`, elegido.texto);
  };

  return (
    <Dialog open onClose={onCerrar} maxWidth="xs" fullWidth>
      <DialogTitle>Insertar enlace</DialogTitle>
      <DialogContent>
        {titulos.length > 0 && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modo}
            onChange={(_, v) => v && setModo(v)}
            sx={{ mb: 2, mt: 0.5 }}
          >
            <ToggleButton value="url">Dirección web</ToggleButton>
            <ToggleButton value="ancla">Sección de esta página</ToggleButton>
          </ToggleButtonGroup>
        )}

        {modo === "url" ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Dirección"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && puede && aplicar()}
          />
        ) : (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Cada título de la página es un ancla. El enlace la sigue apuntando aunque después cambies su redacción.
            </Typography>
            <Box sx={{ maxHeight: 300, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: "5px" }}>
              {titulos.map((t) => (
                <Box
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setAncla(t.id)}
                  onKeyDown={(e) => e.key === "Enter" && setAncla(t.id)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    pl: t.nivel === 3 ? 3.5 : 1.5,
                    cursor: "pointer",
                    bgcolor: ancla === t.id ? tokens.marca.tenue : "transparent",
                    "&:hover": { bgcolor: tokens.marca.tenue },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: t.nivel === 2 ? 600 : 400 }}>{t.texto}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>#{t.id}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onQuitar} sx={{ mr: "auto" }}>
          Quitar enlace
        </Button>
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button variant="contained" disabled={!puede} onClick={aplicar}>
          Insertar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Botón de la barra: `onMouseDown` evita que el editor pierda la selección al pulsar. */
function Herramienta({
  titulo,
  children,
  onPulsar,
  disabled,
}: {
  titulo: string;
  children: React.ReactNode;
  onPulsar: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={titulo}>
      <span>
        <ToggleButton
          value={titulo}
          size="small"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onPulsar}
          sx={{ border: 0, borderRadius: "4px", p: 0.6, color: "text.secondary" }}
        >
          {children}
        </ToggleButton>
      </span>
    </Tooltip>
  );
}
